import { ListSectionHeader, ListSectionMore } from "@rosc/ui/v32/list-section";
import {
  ClipboardList,
  GraduationCap,
  HeartHandshake,
  Mic,
  Plus,
  SearchX,
  TriangleAlert,
} from "lucide-react";
import { motion } from "motion/react";
import type { JSX, ReactNode } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import { Button } from "@/components/ui/button";
import {
  CHIP,
  EmptyState,
  HeaderActionLabel,
  LIST_CARD,
  LoadErrorBanner,
  PAGE_COLUMN,
  PageHeader,
  SearchField,
  TOUCH_BUTTON,
} from "@/components/ui/redesign/page";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import { Skeleton } from "@/components/ui/skeleton";
import { PreChartButton } from "@/components/ui/visits/PreChartSheet";
import { usePreChartSectionCollapsed } from "@/lib/pre-chart-section";
import { cn, container } from "@/lib/utils";
import type { VisitSummary } from "@/services/visits/read-model";
import { VisitListRow } from "./VisitListRow";
import {
  countByFilter,
  dateKindFor,
  filterVisits,
  groupVisits,
  hasReviewCounts,
  PRE_CHART_SECTION_LIMIT,
  VISIT_STATUS_FILTERS,
  type VisitGroupKey,
  type VisitStatusFilter,
} from "./visit-list-model";

const NEW_VISIT_VALIDATIONS = [ValidationSuscriptionButton.RECORDING_USAGES];

interface Props {
  visits: VisitSummary[];
  loading: boolean;
  error: boolean;
  ready: boolean;
  onRetry: () => void;
  onNewVisit: () => void;
  onPreChart?: () => void;
  // Fixed "now" for stories and tests.
  now?: Date;
  // Above the list (the guided first visit's checklist).
  notice?: ReactNode;
}

const capitalize = (value: string): string =>
  value.charAt(0).toLocaleUpperCase() + value.slice(1);

const useGroupLabel = (): ((key: VisitGroupKey) => string) => {
  const { t, i18n } = useTranslation();
  return (key) =>
    key.kind === "month"
      ? capitalize(
          new Date(key.year, key.month, 1).toLocaleDateString(
            i18n.language || "en-US",
            { month: "long", year: "numeric" },
          ),
        )
      : t(`visits.home.groups.${key.kind}`);
};

// "Start New Visit": in the top bar it reads "New visit" on phones.
export const NewVisitButton = ({
  onClick,
  className,
  compact = false,
}: {
  onClick: () => void;
  className?: string;
  compact?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <SubscriptionOnlyButton
      className={cn(
        "h-11 gap-1.5 px-4 lg:h-9",
        compact && "max-sm:px-3",
        className,
      )}
      validations={NEW_VISIT_VALIDATIONS}
      onClick={onClick}
    >
      <Plus aria-hidden />
      {compact ? (
        <HeaderActionLabel
          full={t("visits.newVisitButton")}
          short={t("visits.home.newShort")}
        />
      ) : (
        t("visits.newVisitButton")
      )}
    </SubscriptionOnlyButton>
  );
};

// First visit: what to check before recording, and one clear action.
export const VisitsEmptyState = ({
  onNewVisit,
  onPreChart,
}: {
  onNewVisit: () => void;
  // Pre-chart (VITE_PRE_CHART_ENABLED): prepare a visit before recording.
  onPreChart?: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const tips = [
    { icon: Mic, text: t("visits.emptyState.microphone") },
    { icon: HeartHandshake, text: t("visits.emptyState.consent") },
    { icon: GraduationCap, text: t("visits.emptyState.educate") },
  ];
  return (
    <section
      aria-labelledby="visits-empty-title"
      className="mx-auto max-w-xl rounded-2xl bg-v3-panel shadow-v3-hairline-strong px-5 py-8 text-center sm:px-10 sm:py-12"
      data-testid="visits-empty"
    >
      <span className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-v3-surface-2">
        <Mic aria-hidden className="size-7 text-v3-text-2" />
      </span>
      <h2
        className="mt-5 text-xl font-bold leading-7 sm:text-[22px]"
        id="visits-empty-title"
      >
        {t("visits.emptyStateCTA")}
      </h2>
      <p className="mt-2 text-sm text-v3-text-3">
        {t("visits.emptyState.beforeWeStart")}
      </p>
      <ul className="mt-6 space-y-3 text-left">
        {tips.map(({ icon: Icon, text }) => (
          <li
            key={text}
            className="flex items-start gap-3 rounded-xl bg-v3-surface-2 px-4 py-3 text-sm leading-5 text-v3-text-2"
          >
            <Icon
              aria-hidden
              className="mt-0.5 size-5 shrink-0 text-v3-text-2"
            />
            <span>{text}</span>
          </li>
        ))}
      </ul>
      <div className="mt-8 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
        {onPreChart && (
          <PreChartButton className="w-full sm:w-auto" onClick={onPreChart} />
        )}
        <NewVisitButton className="w-full sm:w-auto" onClick={onNewVisit} />
      </div>
    </section>
  );
};

const LoadingList = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div
      aria-busy
      aria-label={t("visits.home.loading")}
      className={LIST_CARD}
      data-testid="visits-loading"
      role="status"
    >
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <div
          key={index}
          aria-hidden
          className="flex min-h-[72px] items-center gap-4 border-b border-v3-border px-4 last:border-b-0 sm:px-5"
        >
          <Skeleton className="size-10 rounded-lg" role="presentation" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-2/3 max-w-xs" role="presentation" />
            <Skeleton className="h-3 w-32" role="presentation" />
          </div>
        </div>
      ))}
    </div>
  );
};

// Visits home: title and New visit, search and status filters, then the
// visits grouped by day (Today, Yesterday, Last 7 days) and month.
export const VisitsHome = ({
  visits,
  loading,
  error,
  ready,
  onRetry,
  onNewVisit,
  onPreChart,
  now,
  notice,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const groupLabel = useGroupLabel();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<VisitStatusFilter>("all");

  const counts = useMemo(() => countByFilter(visits), [visits]);
  // "Needs review (N)": shown while a visit's note still has unconfirmed
  // "[verify]" values (or while the filter is on), when the list has counts.
  const showReviewFilter =
    hasReviewCounts(visits) &&
    (counts.needsReview > 0 || filter === "needsReview");
  const shown = useMemo(
    () => filterVisits(visits, query, filter),
    [visits, query, filter],
  );
  // Pre-chart drafts pinned in their own foldable section on top.
  const pinPreChart = AppConfig.enablePreChart;
  const groups = useMemo(
    () => groupVisits(shown, now, { pinPreChart }),
    [shown, now],
  );
  const [preChartCollapsed, togglePreChart] = usePreChartSectionCollapsed();
  const [allDrafts, setAllDrafts] = useState(false);
  const empty = ready && visits.length === 0;
  const hasList = ready && visits.length > 0;

  const resetFilters = (): void => {
    setQuery("");
    setFilter("all");
  };

  return (
    <>
      {/* Title, count and New visit are in the top bar (Layout pageHeader). */}
      <PageHeader title={t("visits.title")} />
      <motion.div
        animate="show"
        className={cn(PAGE_COLUMN, "space-y-5 sm:space-y-6")}
        data-testid="visits-home"
        initial="hidden"
        variants={container}
      >
        {notice}
        {error && (
          <LoadErrorBanner
            description={
              ready
                ? t("visits.home.loadError.stale")
                : t("visits.home.loadError.empty")
            }
            title={t("error.unknown")}
            onRetry={onRetry}
          />
        )}

        {loading && !ready && <LoadingList />}

        {empty && !error && (
          <VisitsEmptyState onNewVisit={onNewVisit} onPreChart={onPreChart} />
        )}

        {hasList && (
          <>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <SearchField
                className="lg:w-80 lg:shrink-0"
                clearLabel={t("visits.home.clearSearch")}
                label={t("visits.home.search")}
                testId="visits-search"
                value={query}
                onChange={setQuery}
              />
              {/* Phones: the four status pills share the row without
                  scrolling; "Needs review" goes under them. */}
              <fieldset className="flex min-w-0 flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <legend className="sr-only">
                  {t("visits.home.filterLabel")}
                </legend>
                <div className="flex w-full min-w-0 gap-1.5 sm:w-auto sm:gap-2">
                  {VISIT_STATUS_FILTERS.map((value) => (
                    <button
                      key={value}
                      aria-pressed={filter === value}
                      className={cn(
                        CHIP,
                        "max-sm:flex-auto max-sm:justify-center max-sm:gap-1 max-sm:px-2.5 max-sm:text-[13px]",
                      )}
                      data-testid={`visits-filter-${value}`}
                      type="button"
                      onClick={() => {
                        setFilter(value);
                      }}
                    >
                      {t(`visits.home.filters.${value}`)}
                      <span className="text-xs tabular-nums text-v3-text-3">
                        {counts[value]}
                      </span>
                    </button>
                  ))}
                </div>
                {showReviewFilter && (
                  <button
                    aria-pressed={filter === "needsReview"}
                    className={cn(
                      CHIP,
                      "bg-v3-warn-tint text-v3-warn-text shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--v3-warn)_30%,transparent)] hover:bg-[color-mix(in_srgb,var(--v3-warn)_20%,transparent)] aria-pressed:bg-v3-warn aria-pressed:text-v3-panel aria-pressed:shadow-none",
                    )}
                    data-testid="visits-filter-needsReview"
                    title={t("visits.home.needsReviewHint")}
                    type="button"
                    onClick={() => {
                      setFilter(
                        filter === "needsReview" ? "all" : "needsReview",
                      );
                    }}
                  >
                    <TriangleAlert
                      aria-hidden
                      className="size-4 text-amber-600 dark:text-amber-300"
                    />
                    {t("visits.home.filters.needsReview")}
                    <span className="text-xs font-semibold tabular-nums">
                      {counts.needsReview}
                    </span>
                  </button>
                )}
              </fieldset>
            </div>

            {groups.length === 0 ? (
              <EmptyState
                description={
                  query.trim()
                    ? t("visits.home.noResults.description")
                    : t("visits.home.noResults.statusDescription")
                }
                icon={SearchX}
                testId="visits-no-results"
                title={t("visits.home.noResults.title")}
              >
                <Button
                  className={TOUCH_BUTTON}
                  type="button"
                  variant="outline"
                  onClick={resetFilters}
                >
                  {t("visits.home.noResults.reset")}
                </Button>
              </EmptyState>
            ) : (
              <div className="space-y-6">
                {groups.map((group) => {
                  const label = groupLabel(group.key);
                  const pinned = group.key.kind === "preChart";
                  const folded = pinned && preChartCollapsed;
                  const trimmed =
                    pinned && group.visits.length > PRE_CHART_SECTION_LIMIT;
                  const rows =
                    trimmed && !allDrafts
                      ? group.visits.slice(0, PRE_CHART_SECTION_LIMIT)
                      : group.visits;
                  return (
                    <section
                      key={group.id}
                      aria-labelledby={`visits-${group.id}`}
                      data-testid={
                        pinned ? "visits-home-pre-chart-section" : undefined
                      }
                    >
                      {pinned ? (
                        <ListSectionHeader
                          controls={`visits-list-${group.id}`}
                          count={group.visits.length}
                          expanded={!folded}
                          icon={<ClipboardList />}
                          id={`visits-${group.id}`}
                          size="home"
                          testId="visits-home-pre-chart-toggle"
                          title={label}
                          onToggle={togglePreChart}
                        />
                      ) : (
                        <h2
                          className="px-1 pb-2 font-v3 text-[12.5px] font-medium text-v3-text-3"
                          id={`visits-${group.id}`}
                        >
                          {label}
                        </h2>
                      )}
                      {!folded && (
                        <ul
                          aria-label={t("visits.home.listLabel", {
                            group: label,
                          })}
                          className={cn(LIST_CARD, "divide-y")}
                          id={`visits-list-${group.id}`}
                        >
                          {rows.map((visit) => (
                            <VisitListRow
                              key={visit.public_id}
                              group={dateKindFor(visit, group.key.kind, now)}
                              visit={visit}
                            />
                          ))}
                          {trimmed && (
                            <ListSectionMore
                              controls={`visits-list-${group.id}`}
                              expanded={allDrafts}
                              label={
                                allDrafts
                                  ? t("visits.home.preChartShowLess")
                                  : t("visits.home.preChartShowAll", {
                                      count: group.visits.length,
                                    })
                              }
                              testId="visits-home-pre-chart-more"
                              variant="card"
                              onToggle={() => setAllDrafts((value) => !value)}
                            />
                          )}
                        </ul>
                      )}
                    </section>
                  );
                })}
              </div>
            )}
          </>
        )}
      </motion.div>
    </>
  );
};
